import { memo, useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useLocation, useNavigate, useRouter } from "@tanstack/react-router"
import {
	IconPlusOutline18 as PlusIcon,
	IconSliders3Outline18 as SettingsIcon,
} from "nucleo-ui-outline-18"
import { create } from "zustand"

import { cn } from "@productlane/lib/src/browser"
import { IconButton, Tooltip } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import {
	NavSectionBody,
	NavSectionHeader,
} from "@/components/shared/nav-section-header"
import { toast } from "@/components/toast"
import { useIsAgentPageAvailable } from "@/lib/hooks/use-is-agent-page-available"
import {
	INBOX_SECTIONS,
	useInboxNavStore,
	useIsInboxSectionOpen,
} from "@/lib/states/inbox-nav-state"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import {
	INBOX_NAV_GROUP,
	INBOX_NAV_SECTION_HEADER,
} from "../inbox/components/inbox-nav-styles"
import { isAgentTurnedOff } from "./agent-off-state"
import {
	activeRowId,
	AgentIdeaRows,
	visibleAgentRows,
} from "./agent-sidebar-rows"
import { assistantSidebarRows } from "./assistant-chats"
import { useAgentItems } from "./use-agent-recommendations"

// Enough for View more; the View all page loads its own longer list.
const SIDEBAR_CHAT_LIMIT = 30

const HEADER_ACTION =
	"group/add h-4 w-4 shrink-0 transform-gpu text-tertiary hover:bg-transparent hover:text-secondary"
// Settings waits for hover of the header row; New chat stays visible.
const SETTINGS_ACTION = cn(
	HEADER_ACTION,
	"opacity-0 focus-visible:opacity-100 group-hover/section:opacity-100 data-[state=open]:opacity-100",
)
// IconButton paints its ghost icon itself, so the icon follows the button's hover.
const HEADER_ACTION_ICON = "h-3.5 w-3.5 group-hover/add:text-secondary"

export function useIsAgentSidebarShown(): boolean {
	const available = useIsAgentPageAvailable()
	const workspace = useZeroWorkspace()
	const { role } = useRole()
	// The Agent page is closed to viewers, so its links would go nowhere.
	return (
		available === true &&
		role !== "VIEWER" &&
		!isAgentTurnedOff(workspace?.aiChatSettings)
	)
}

export const AgentSidebarSection = memo(() => {
	if (!useIsAgentSidebarShown()) return null
	return <AgentSidebarList />
})

// Shared with the sidebar's Tab order, so the keys reach every row on screen.
const useAgentSidebarExpanded = create<{
	expanded: boolean
	toggle: () => void
}>()((set) => ({
	expanded: false,
	toggle: () => set((state) => ({ expanded: !state.expanded })),
}))

function useAgentSidebarRows() {
	const { items } = useAgentItems()
	const [chats] = useQuery(
		queries.myAssistantChats({ archived: false, limit: SIDEBAR_CHAT_LIMIT }),
	)
	return useMemo(() => assistantSidebarRows(items, chats), [items, chats])
}

/** The Assistant rows on screen and the open one, for Tab and Shift+Tab. */
export function useAgentSidebarNav(): {
	rows: { id: string; href: string }[]
	activeId: string | null
} {
	const shown = useIsAgentSidebarShown()
	const isOpen = useIsInboxSectionOpen(INBOX_SECTIONS.agent)
	const expanded = useAgentSidebarExpanded((s) => s.expanded)
	const rows = useAgentSidebarRows()
	const { pathname, searchStr } = useLocation()
	if (!shown || !isOpen) return { rows: [], activeId: null }
	const visible = visibleAgentRows(rows, expanded)
	return { rows: visible, activeId: activeRowId(pathname, searchStr) }
}

/** "+" opens a fresh chat even from /agent/new: the screen keys the chat on the history entry, which a same-URL navigate keeps. */
export function useStartNewChat() {
	const navigate = useNavigate()
	const router = useRouter()
	const pathname = useLocation({ select: (l) => l.pathname })
	return () => {
		if (pathname === "/agent/new") router.history.replace("/agent/new")
		else
			void navigate({
				to: "/agent/$tab",
				params: { tab: "new" },
			})
	}
}

const AgentSidebarList = memo(() => {
	const navigate = useNavigate()
	const startNewChat = useStartNewChat()
	const { pathname, searchStr } = useLocation()
	const zero = useZero()
	const isOpen = useIsInboxSectionOpen(INBOX_SECTIONS.agent)
	const toggleSection = useInboxNavStore((s) => s.toggleSection)
	const expanded = useAgentSidebarExpanded((s) => s.expanded)
	const toggleExpanded = useAgentSidebarExpanded((s) => s.toggle)
	const rows = useAgentSidebarRows()

	const archive = (chatId: string) => {
		mutateOk(
			zero.mutate(
				mutators.aiChatSessions.archive({ id: chatId, archived: true }),
			),
		).then(
			() =>
				toast.success({
					title: "Chat archived",
					action: {
						label: "Undo",
						callback: () =>
							void zero.mutate(
								mutators.aiChatSessions.archive({
									id: chatId,
									archived: false,
								}),
							),
					},
				}),
			() => toast.error({ title: "Couldn't archive this chat" }),
		)
	}

	return (
		<div
			data-testid="agent-sidebar-section"
			className={cn("group/inbox-section mt-4 max-sm:hidden", INBOX_NAV_GROUP)}
		>
			<NavSectionHeader
				label="Assistant"
				hoverGroup="inbox-section"
				isOpen={isOpen}
				onToggle={() => toggleSection(INBOX_SECTIONS.agent)}
				className={INBOX_NAV_SECTION_HEADER}
				action={
					<div className="mr-[7px] flex items-center gap-1.5">
						<DropdownMenu
							className="w-[180px]"
							contentProps={{ align: "end" }}
							trigger={
								<div>
									<Tooltip label="Assistant settings" side="bottom">
										<IconButton
											Icon={SettingsIcon}
											variant="ghost"
											aria-label="Assistant settings"
											iconStrokeWidth={1}
											className={SETTINGS_ACTION}
											iconClassName={HEADER_ACTION_ICON}
										/>
									</Tooltip>
								</div>
							}
						>
							<DropdownMenuItem
								onSelect={() =>
									void navigate({
										to: "/agent/$tab",
										params: { tab: "chats" },
									})
								}
							>
								View all
							</DropdownMenuItem>
						</DropdownMenu>
						<IconButton
							Icon={PlusIcon}
							variant="ghost"
							aria-label="New chat with the assistant"
							onClick={startNewChat}
							iconStrokeWidth={1}
							className={HEADER_ACTION}
							iconClassName={HEADER_ACTION_ICON}
						/>
					</div>
				}
			/>
			<NavSectionBody isOpen={isOpen}>
				<AgentIdeaRows
					rows={rows}
					activeId={activeRowId(pathname, searchStr)}
					expanded={expanded}
					onToggleExpanded={toggleExpanded}
					onArchive={archive}
				/>
			</NavSectionBody>
		</div>
	)
})
